/**
 * @file 试卷打印预览
 * @description 在对话框中生成分页预览，底部「立即打印」调用浏览器打印。
 *
 * @whenToUse 讲义打印页点击「打印」之后。
 * @whenNotToUse 不需要系统打印弹窗的只读查看。
 */

import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useDialogDeferredContent } from '@/components/ui/use-dialog-deferred-content.ts'
import { KnowledgePrintTemplate } from '@/components/pages/handout-page/print/knowledge-print-template.tsx'
import { KnowledgePrintTemplateLite } from '@/components/pages/handout-page/print/knowledge-print-template-lite.tsx'
import {
  buildHandoutPaper,
  type HandoutPaperModel,
} from '@/components/pages/handout-page/print/paper-data.ts'
import { PrintTemplate } from '@/components/pages/handout-page/print/print-template.tsx'
import type { HandoutPrintRow } from '@/components/pages/handout-page/types.ts'

/**
 * 按模板渲染试卷。
 * @param model 试卷数据
 * @param onReady 分页是否结束
 */
function PaperPreviewBody({
  model,
  onReady,
}: {
  model: HandoutPaperModel
  onReady: (ready: boolean) => void
}) {
  if (model.kind === 'knowledge') return <KnowledgePrintTemplate model={model} onReady={onReady} />
  if (model.kind === 'knowledge-lite') {
    return <KnowledgePrintTemplateLite model={model} onReady={onReady} />
  }
  return <PrintTemplate model={model} onReady={onReady} />
}

/**
 * 对话框内的试卷预览。分页在模板内完成，立即打印拉起系统打印。
 */
export function HandoutPrintPreview({
  open,
  row,
  onClose,
  onPrinted,
  printAnswers,
}: {
  /** 是否打开 */
  open: boolean
  /** 当前申请；关闭动画结束后由父级清空 */
  row: HandoutPrintRow | null
  /** 关闭预览 */
  onClose: () => void
  /** 用户确认拉起系统打印 */
  onPrinted: () => void
  /** 是否渲染答案页与答案二维码 */
  printAnswers: boolean
}) {
  const displayRow = useDialogDeferredContent(open, row)
  const model = useMemo(
    () => (displayRow ? buildHandoutPaper(displayRow, printAnswers) : null),
    [displayRow, printAnswers],
  )
  const [ready, setReady] = useState(false)

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={displayRow ? `${displayRow.studentName} · 试卷预览` : '试卷预览'}
      className="paper-preview-dialog h-[85vh] max-h-[85vh] min-h-0 max-w-6xl overflow-hidden"
      contentClassName="paper-preview-stage"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            关闭
          </Button>
          <Button
            disabled={!ready}
            onClick={() => {
              window.print()
              onPrinted()
            }}
          >
            立即打印
          </Button>
        </>
      }
    >
      {model ? (
        <>
          <p className="paper-preview-chrome mb-3 text-xs text-muted">
            {ready ? '预览已生成，可立即打印。' : '正在生成试卷预览…'}
          </p>
          <PaperPreviewBody model={model} onReady={setReady} />
        </>
      ) : null}
    </Dialog>
  )
}
